import React, { useMemo, memo, useCallback } from 'react';
import { Stack, Box, Typography, Button, useTheme, TextField, InputAdornment } from '@mui/material';
import { isEqual } from 'lodash';
import { OBJECT_OF_PLAY, TrackedEntityArea } from '@/shared/hooks/useServerState';

import { useAutoIsoTeamSelector } from '../context/AutoIsoTeamSelectorContext';
import TeamPlayersList from './TeamPlayersList';
import { AutoIsoObjectType, AutoIsoPlayer } from '@/features/autoIso/types/AutoIsoModel';
import { ProcessedTeam } from '../types/AutoIso';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';

// Memoized team item component to prevent unnecessary re-renders
const TeamItem = memo(
  ({
    team,
    index,
    trackEntity,
    singleCurrentlyTrackedObjectId,
    showGroupTitles
  }: {
    team: ProcessedTeam;
    index: number;
    trackEntity: (ent: any) => void;
    singleCurrentlyTrackedObjectId: number | null;
    showGroupTitles: boolean;
  }) => {
    // Memoize players array to prevent recreation on every render
    const players = useMemo(
      () => Object.values(team.players).flat() as AutoIsoPlayer[],
      [team.players],
    );

    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          overflow: 'hidden',
          minHeight: 0,
        }}
      >
        <TeamPlayersList
          showGroupTitles={showGroupTitles}
          index={index}
          team={team as any}
          players={players}
          onPlayerClick={(p) => trackEntity(p)}
          singleCurrentlyTrackedObjectId={singleCurrentlyTrackedObjectId}
          sx={{ minWidth: '100px', height: '100%' }}
        />
      </Box>
    );
  },
  (prevProps, nextProps) => {
    // Only re-render if team data or tracked object actually changed.
    // `trackEntity` and `showGroupTitles` both change with the browse toggle,
    // so they have to be compared: the two rosters can hold equal player data
    // (every registered player on surface, or both empty), and skipping the
    // re-render then leaves the child holding the previous mode's callback —
    // reporting `Players on Surface` for a click in `Browse All Players`.
    return (
      prevProps.team.id === nextProps.team.id &&
      prevProps.index === nextProps.index &&
      prevProps.singleCurrentlyTrackedObjectId === nextProps.singleCurrentlyTrackedObjectId &&
      prevProps.showGroupTitles === nextProps.showGroupTitles &&
      prevProps.trackEntity === nextProps.trackEntity &&
      isEqual(prevProps.team.players, nextProps.team.players)
    );
  },
);

export const AutoISOTeamSelector: React.FC<{ sx?: object }> = ({ sx }) => {
  const { processedTeams } = useAutoIsoTeamSelector();
  const { fullProcessedTeams, surfaceName, objectOfPlay } = useCurrentGame();

  
  // Server State Context
  const { trackEntity, selectedObjectType, singleCurrentlyTrackedObjectId } =
    useServerStateContext();

  const [browseAllPlayers, setBrowseAllPlayers] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  // The board separates these two rosters as distinct Areas. They render from
  // the same component, so the browse toggle is the only thing that tells them
  // apart — without this both emitted the same free-text source.
  const area: TrackedEntityArea = browseAllPlayers ? 'Browse All Players' : 'Players on Surface';

  const selectPuckOrBallTracking = useCallback(() => {
    trackEntity(OBJECT_OF_PLAY, 'AutoISOTeamSelector', area);
  }, [trackEntity, area]);
  const selectPlayer = useCallback(
    (ent: any) => {
      trackEntity(ent, 'AutoISOTeamSelector', area);
    },
    [trackEntity, area],
  );

  const ballOrPuckSelected = useMemo(() => {
    return selectedObjectType === AutoIsoObjectType.BallOrPuck;
  }, [selectedObjectType]);

  // Switch between on-ice players and full roster based on browse mode
  const displayedTeams = useMemo(() => {
    return browseAllPlayers ? fullProcessedTeams : processedTeams;
  }, [browseAllPlayers, fullProcessedTeams, processedTeams]);

  // Filter teams based on search query when browsing all players
  const filteredTeams = useMemo(() => {
    // If not browsing all players or no search query, return teams as-is
    if (!browseAllPlayers || !searchQuery.trim()) {
      return displayedTeams;
    }

    const query = searchQuery.toLowerCase().trim();

    // Filter each team's players based on the search query
    return displayedTeams.map((team: ProcessedTeam) => {
      const filteredPlayers = Object.entries(team.players).reduce((acc, [position, players]) => {
        const matchingPlayers = (players as AutoIsoPlayer[]).filter((player) =>
          player.displayName.toLowerCase().includes(query) ||
          player.playerNumber?.toLowerCase().includes(query)
        );
        if (matchingPlayers.length > 0) {
          acc[position] = matchingPlayers;
        }
        return acc;
      }, {} as typeof team.players);

      return {
        ...team,
        players: filteredPlayers,
      };
    });
  }, [displayedTeams, browseAllPlayers, searchQuery]);

  const theme = useTheme();

  return (
    <Stack
      spacing={0}
      sx={{
        p: 1,
        backgroundColor: '#101013',
        borderRadius: 1,
        height: '100%',
        overflow: 'hidden',
        ...sx,
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        sx={{ mb: 1, minHeight: '10px', flexShrink: 0, alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Typography
            sx={(theme) => ({
              color: browseAllPlayers ? theme.palette.cx.text.muted : '#FFFFFF',
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '16px',
              letterSpacing: '0px',
              '&:hover': { cursor: 'pointer' },
              textTransform: 'capitalize',
            })}
            onClick={() => setBrowseAllPlayers(false)}
          >
            Players On {surfaceName || 'Ice'}
          </Typography>
          <Typography
            onClick={() => setBrowseAllPlayers(!browseAllPlayers)}
            sx={(theme) => ({
              color: browseAllPlayers ? '#FFFFFF' : theme.palette.cx.text.muted,
              fontWeight: 500,
              fontSize: '14px',
              lineHeight: '16px',
              letterSpacing: '0px',
              '&:hover': { cursor: 'pointer' },
            })}
          >
            Browse All Players
          </Typography>
        </Box>
        <Stack>
          <Button
            sx={{
              color: ballOrPuckSelected ? theme.palette.cx.bg.elevated : theme.palette.cx.light[200],
              backgroundColor: ballOrPuckSelected ? theme.palette.cx.light[200] : theme.palette.cx.bg.elevated,
              textTransform: 'none',
              fontWeight: 400,
              fontSize: '12px',
              lineHeight: '14px',
              letterSpacing: '0px',
              borderRadius: 1,
              padding: '4px 4px',
              marginRight: 1.3,
            }}
            variant="text"
            onClick={selectPuckOrBallTracking}
          >
            {objectOfPlay || 'Puck'}
          </Button>
        </Stack>
      </Stack>


      {browseAllPlayers ? (
        <Box>
          {/* Implement Browse All Players List Here */}
          <TextField
            variant="outlined"
            size="small"
            placeholder="Search Players..."
            fullWidth
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Box sx={{ color: theme.palette.cx.text.secondary, display: 'flex', alignItems: 'center' }}>
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M6.12467 2.04159C3.86951 2.04159 2.04134 3.86976 2.04134 6.12492C2.04134 8.38008 3.86951 10.2083 6.12467 10.2083C7.25234 10.2083 8.27277 9.75153 9.01203 9.01227C9.75129 8.27301 10.208 7.25258 10.208 6.12492C10.208 3.86976 8.37984 2.04159 6.12467 2.04159ZM1.45801 6.12492C1.45801 3.54759 3.54735 1.45825 6.12467 1.45825C8.702 1.45825 10.7913 3.54759 10.7913 6.12492C10.7913 7.30852 10.3504 8.38959 9.62433 9.21209L12.4559 12.0437C12.5698 12.1576 12.5698 12.3423 12.4559 12.4562C12.342 12.5701 12.1573 12.5701 12.0434 12.4562L9.21185 9.62457C8.38935 10.3506 7.30827 10.7916 6.12467 10.7916C3.54735 10.7916 1.45801 8.70225 1.45801 6.12492Z"
                          fill="currentColor"
                        />
                      </svg>
                    </Box>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              mb: 1,
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.cx.bg.elevated,
                color: theme.palette.cx.light[100],
                fontSize: '12px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.cx.border.default,
              },
              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '& .MuiInputBase-input::placeholder': {
                color: theme.palette.cx.text.secondary,
                opacity: 1,
              },
            }}
          />
          
        </Box>
      ) : null}

      {/* Team Lists - Side by Side */}
      <Box sx={{ display: 'flex', overflow: 'hidden', flex: 1, minHeight: 0 }}>
        {filteredTeams.map((team: ProcessedTeam, index: number) => (
          <TeamItem
            showGroupTitles={browseAllPlayers}
            key={team.id}
            team={team}
            index={index}
            trackEntity={selectPlayer}
            singleCurrentlyTrackedObjectId={singleCurrentlyTrackedObjectId}
          />
        ))}
      </Box>
    </Stack>
  );
};

export default AutoISOTeamSelector;
